import {
  Button,
  DatePicker,
  DatePickerProps,
  Input,
  message,
  Modal,
  Space,
  Table,
  TimeRangePickerProps,
  Select,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Form,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { displayDate } from '../../../utils/datetime.util';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarOutlined,
  MailOutlined,
  FileTextOutlined,
  SettingOutlined,
} from '@ant-design/icons';

import { AppDispatch } from '../../../store/store';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  downloadReportThunk,
  generateAgingReportThunk,
  generateCollectionReportThunk,
  generateCustomerDetailsReportThunk,
  generateInterestChargesReportThunk,
  generateKpktReportThunk,
  generateLateChargesReportThunk,
  generateLoanInsuranceReportThunk,
  generateMasterListingReportThunk,
  generateMiscBillingReportThunk,
  generateOverDueReportThunk,
  generatePartnerReportThunk,
  generateReportTaskThunk,
  generateSmeMonthlyDeductionReportThunk,
  generateStampDutyReportThunk,
  generateWaiverReportThunk,
} from '../../../services/reportService/reportThunk';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import startCase from 'lodash.startcase';
import moment from 'moment';
import { emailValidator } from '../../../utils/validation';
import { ReportTypeLabel } from '../../../constant/report';
import { ReportTypeEnum } from '../../../enum/report';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

type ReportTableProps = {
  dataSource: any;
  callback: any;
  initialParams: any;
  type: string;
};

const REPORT_TYPES_WITH_FINANCING_TYPE = [
  ReportTypeEnum.MASTER_LISTING,
  ReportTypeEnum.INTEREST_CHARGE,
  ReportTypeEnum.MISC_BILLING,
  ReportTypeEnum.LOAN_MATURITY,
  ReportTypeEnum.OVERDUE,
  ReportTypeEnum.COLLECTION,
  ReportTypeEnum.CUSTOMER_LOAN,
  ReportTypeEnum.CUSTOMER_LOAN_INTEREST,
  ReportTypeEnum.LATE_CHARGES,
  ReportTypeEnum.PARTNER_INCENTIVE,
  ReportTypeEnum.AGING_REPORT,
  ReportTypeEnum.CUSTOMER_DETAILS,
  ReportTypeEnum.STAMP_DUTY,
  ReportTypeEnum.WAIVER_REPORT,
  ReportTypeEnum.EARLY_SETTLED,
];

const REPORT_TYPES_WITH_LOAN_TYPE = [
  ReportTypeEnum.MASTER_LISTING,
  ReportTypeEnum.INTEREST_CHARGE,
  ReportTypeEnum.MISC_BILLING,
  ReportTypeEnum.LOAN_MATURITY,
  ReportTypeEnum.OVERDUE,
  ReportTypeEnum.COLLECTION,
  ReportTypeEnum.CUSTOMER_LOAN,
  ReportTypeEnum.CUSTOMER_LOAN_INTEREST,
  ReportTypeEnum.LATE_CHARGES,
  ReportTypeEnum.AGING_REPORT,
  ReportTypeEnum.CUSTOMER_DETAILS,
  ReportTypeEnum.STAMP_DUTY,
  ReportTypeEnum.SME_MONTHLY_DEDUCTION,
  ReportTypeEnum.WAIVER_REPORT,
  ReportTypeEnum.EARLY_SETTLED,
];

const ReportTable: React.FC<ReportTableProps> = (props) => {
  const { dataSource, callback, initialParams, type } = props;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [form] = Form.useForm();

  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [downloading, setDownloading] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [reportDate, setReportDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [email, setEmail] = useState('');
  const [financingType, setFinancingType] = useState('CONVENTIONAL');
  const [loanType, setLoanType] = useState('CUSTOMER');

  // Helper function to get report type display name
  const getReportTypeDisplayName = (type: string) => {
    return ReportTypeLabel[type as ReportTypeEnum] || startCase(type.toLowerCase());
  };

  // Helper function to determine if date range is required
  const requiresDateRange = (type: string) => {
    return [
      'COLLECTION',
      'KPKT',
      'INTEREST_CHARGE',
      'MISC_BILLING',
      'LOAN_MATURITY',
      'LATE_CHARGES',
      'PARTNER_INCENTIVE',
      'CUSTOMER_LOAN_INTEREST',
      'LOAN_INSURANCE',
    ].includes(type);
  };

  // Helper function to determine if single date is required
  const requiresSingleDate = (type: string) => {
    return ['MASTER_LISTING', 'OVERDUE', 'AGING_REPORT', 'CUSTOMER_DETAILS', 'STAMP_DUTY'].includes(
      type
    );
  };

  // Helper function to determine if month picker is required
  const requiresMonthPicker = (type: string) => {
    return ['SME_MONTHLY_DEDUCTION', 'WAIVER_REPORT', 'CUSTOMER_LOAN'].includes(type);
  };

  // Helper function to determine if email is required
  const requiresEmail = (type: string) => {
    return [
      'AGING_REPORT',
      'CUSTOMER_LOAN',
      'CUSTOMER_LOAN_INTEREST',
      'LOAN_MATURITY',
      'SME_MONTHLY_DEDUCTION',
      'LOAN_INSURANCE',
    ].includes(type);
  };

  const onDownloadReport = (id: any, fileName: string) => {
    setDownloading(true);
    dispatch(downloadReportThunk({ id }))
      .unwrap()
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        setTimeout(function () {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
        }, 100);
        message.success('Successfully download report.');
      })
      .catch((error) => message.error('Failed to download report. Please try again later.'))
      .finally(() => setDownloading(false));
  };

  const tablePagination = {
    total: dataSource?.data?.total ?? 0,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Date',
      dataIndex: 'reportDate',
      key: 'reportDate',
      render: (text) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
    },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button onClick={() => onDownloadReport(record.fileId, record.fileName)}>Download</Button>
        </Space>
      ),
    },
  ];

  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ];

  const onChangeDate = (date: any) => {
    const selectedDate_ = {
      startDate: date[0].startOf('days').format(),
      endDate: date[1].endOf('days').format(),
    };
    callback({
      ...initialParams,
      filters: {
        startRangeDate: selectedDate_.startDate,
        endRangeDate: selectedDate_.endDate,
      },
    });
  };

  const getLoanTypeOptions = (type: ReportTypeEnum) => {
    let loanTypeOptions = [
      {
        value: 'CUSTOMER',
        label: 'Customer',
      },
      {
        value: 'STAFF',
        label: 'Staff',
      },
      {
        value: 'PARTNER',
        label: 'Partner',
      },
      {
        value: 'ALL',
        label: 'All',
      },
    ];
    if ([ReportTypeEnum.SME_MONTHLY_DEDUCTION].includes(type)) {
      loanTypeOptions = loanTypeOptions.filter((loanType) => loanType.value !== 'CUSTOMER');
      if (loanType === 'CUSTOMER') {
        setLoanType('STAFF');
      }
    }
    return loanTypeOptions;
  };

  const handleTableChange = async (pagination: TablePaginationConfig) => {
    callback({ ...initialParams, pagination });
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });

    queryParams.set('pageNum', `${pagination.current ?? 1}`);
    queryParams.set('pageSize', `${pagination.pageSize ?? 10}`);
    const newSearch = queryParams.toString();
    navigate({
      pathname: location.pathname,
      search: newSearch,
    });
  };

  const openDateModal = async () => {
    setIsDateModalOpen(true);
    // Reset form when opening modal
    form.resetFields();
  };

  const generateReport = async () => {
    try {
      // Validate form fields
      await form.validateFields();

      setIsDateModalOpen(false);

      // check the email input for reports that require email
      if (requiresEmail(type)) {
        emailValidator(email).catch((error) => {
          message.error('Invalid Email');
          return;
        });
      }

      // ... rest of your generateReport logic remains the same
      switch (type) {
        case 'MASTER_LISTING':
          await dispatch(
            generateMasterListingReportThunk({
              reportDate,
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(reportDate).format('YYYY_MM_DD')}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));
          break;
        case 'COLLECTION':
          await dispatch(
            generateCollectionReportThunk({
              startDate,
              endDate,
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(startDate).format('YYYY_MM_DD')}-${moment(endDate).format(
                  'YYYY_MM_DD'
                )}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));
          break;

        case 'KPKT':
          await dispatch(
            generateKpktReportThunk({
              startDate,
              endDate,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(startDate).format('YYYY_MM_DD')}-${moment(endDate).format(
                  'YYYY_MM_DD'
                )}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));
          break;

        case 'INTEREST_CHARGE':
          await dispatch(
            generateInterestChargesReportThunk({
              startDate,
              endDate,
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(startDate).format('YYYY_MM_DD')}-${moment(endDate).format(
                  'YYYY_MM_DD'
                )}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));
          break;

        case 'MISC_BILLING':
          await dispatch(
            generateMiscBillingReportThunk({
              startDate,
              endDate,
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(startDate).format('YYYY_MM_DD')}-${moment(endDate).format(
                  'YYYY_MM_DD'
                )}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));
          break;

        case 'LOAN_MATURITY':
          await dispatch(
            generateReportTaskThunk({
              reportType: ReportTypeEnum.LOAN_MATURITY,
              startDate,
              endDate,
              financingType,
              loanType,
              email,
            })
          )
            .unwrap()
            .then((res) => {
              message.info(
                res?.message || 'Report is being generated. Please check your email later.'
              );
            })
            .catch((error) =>
              message.error(error?.message || 'Failed to generate report. Please try again later.')
            )
            .finally(() => setDownloading(false));
          break;

        case 'CUSTOMER_LOAN':
          await dispatch(
            generateReportTaskThunk({
              reportType: ReportTypeEnum.CUSTOMER_LOAN,
              startDate: moment(reportDate).startOf('month').format('YYYY-MM-DD'),
              endDate: moment(reportDate).endOf('month').format('YYYY-MM-DD'),
              financingType,
              loanType,
              email,
            })
          )
            .unwrap()
            .then((res) => {
              message.info(
                res?.message || 'Report is being generated. Please check your email later.'
              );
            })
            .catch((error) =>
              message.error(error?.message || 'Failed to generate report. Please try again later.')
            )
            .finally(() => setDownloading(false));
          break;

        case 'CUSTOMER_LOAN_INTEREST':
          await dispatch(
            generateReportTaskThunk({
              reportType: ReportTypeEnum.CUSTOMER_LOAN_INTEREST,
              startDate,
              endDate,
              financingType,
              loanType,
              email,
            })
          )
            .unwrap()
            .then((res) => {
              message.info(
                res?.message || 'Report is being generated. Please check your email later.'
              );
            })
            .catch((error) =>
              message.error(error?.message || 'Failed to generate report. Please try again later.')
            )
            .finally(() => setDownloading(false));
          break;

        case 'LATE_CHARGES':
          await dispatch(
            generateLateChargesReportThunk({
              startDate,
              endDate,
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(startDate).format('YYYY_MM_DD')}-${moment(endDate).format(
                  'YYYY_MM_DD'
                )}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));
          break;

        case 'PARTNER_INCENTIVE':
          await dispatch(
            generatePartnerReportThunk({
              startDate,
              endDate,
              financingType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(startDate).format('YYYY_MM_DD')}-${moment(endDate).format(
                  'YYYY_MM_DD'
                )}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));
          break;

        case 'OVERDUE':
          await dispatch(
            generateOverDueReportThunk({
              endDate: reportDate,
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(reportDate).format('YYYY_MM_DD')}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));

          break;

        case 'AGING_REPORT':
          await dispatch(
            generateAgingReportThunk({
              startDate: reportDate,
              email,
              isSave: false,
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              message.info('Report is being generated. Please check your email later.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));

          break;

        case 'CUSTOMER_DETAILS':
          await dispatch(
            generateCustomerDetailsReportThunk({
              startDate: reportDate,
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(reportDate).format('YYYY_MM_DD')}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));

          break;

        case 'STAMP_DUTY':
          await dispatch(
            generateStampDutyReportThunk({
              reportDate,
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment(reportDate).format('YYYY_MM_DD')}_report_${startCase(type)}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => message.error('Failed to download report. Please try again later.'))
            .finally(() => setDownloading(false));

          break;

        case 'LOAN_INSURANCE':
          setDownloading(true);
          await dispatch(
            generateLoanInsuranceReportThunk({
              startDate: moment(startDate).startOf('day').toISOString(),
              endDate: moment(endDate).endOf('day').toISOString(),
              email,
            })
          )
            .unwrap()
            .then((res) => {
              message.info(
                res?.message || 'Report is being generated. Please check your email later.'
              );
            })
            .catch((error) =>
              message.error(error?.message || 'Failed to generate report. Please try again later.')
            )
            .finally(() => setDownloading(false));

          break;

        case 'SME_MONTHLY_DEDUCTION':
          setDownloading(true);

          await dispatch(
            generateReportTaskThunk({
              reportType: ReportTypeEnum.SME_MONTHLY_DEDUCTION,
              startDate: moment(reportDate).endOf('month').toISOString(),
              endDate,
              financingType,
              loanType,
              email,
            })
          )
            .unwrap()
            .then((res) => {
              message.info(
                res?.message || 'Report is being generated. Please check your email later.'
              );
            })
            .catch((error) =>
              message.error(error?.message || 'Failed to generate report. Please try again later.')
            )
            .finally(() => setDownloading(false));
          break;

        case 'WAIVER_REPORT':
          setDownloading(true);
          await dispatch(
            generateWaiverReportThunk({
              reportDate: moment(reportDate).endOf('month').toISOString(),
              financingType,
              loanType,
            })
          )
            .unwrap()
            .then((res) => {
              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', res.name);
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');
            })
            .catch((error) => {
              console.log(error);
              message.error('Failed to download report.');
            })
            .finally(() => setDownloading(false));
          break;
      }
    } catch (error) {
      console.log('Validation failed:', error);
    }
  };

  const reportDateOnChange: DatePickerProps['onChange'] = (date, dateString) => {
    setReportDate(dateString);
  };

  const rangeDateOnChange = (date: any) => {
    setStartDate(date[0].format('YYYY-MM-DD'));
    setEndDate(date[1].format('YYYY-MM-DD'));
  };

  return (
    <>
      <div className="flex justify-between mb-4">
        {type !== 'KPKT' && (
          <>
            <Input.Group compact>
              <RangePicker presets={rangePresets} onChange={onChangeDate} style={{ width: 250 }} />
            </Input.Group>
          </>
        )}

        <Button
          type="primary"
          onClick={() => openDateModal()}
          loading={downloading}
          icon={<FileTextOutlined />}
          size="large"
        >
          Generate Report
        </Button>
      </div>

      <br />
      {downloading && (
        <div className="absolute w-full h-full z-50 backdrop-brightness-95">
          <LoadingSpinner />
        </div>
      )}

      <Table
        rowKey="id"
        className="min-w-fit"
        columns={columns}
        pagination={tablePagination}
        dataSource={dataSource?.data ?? []}
        loading={dataSource?.isLoading}
        onChange={handleTableChange}
      />

      {/* Enhanced Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <FileTextOutlined className="text-blue-600" />
            <Title level={4} className="mb-0">
              Generate {getReportTypeDisplayName(type)} Report
            </Title>
          </div>
        }
        open={isDateModalOpen}
        onCancel={() => setIsDateModalOpen(false)}
        onOk={generateReport}
        width={600}
        okText="Generate Report"
        cancelText="Cancel"
        okButtonProps={{
          icon: <FileTextOutlined />,
          size: 'large',
        }}
        cancelButtonProps={{
          size: 'large',
        }}
      >
        <div className="py-4">
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              financingType: 'CONVENTIONAL',
              loanType: loanType || 'CUSTOMER',
            }}
          >
            {/* Date Selection Section */}
            <Card className="mb-4" size="small">
              <div className="flex items-center gap-2 mb-3">
                <CalendarOutlined className="text-blue-600" />
                <Text strong>Date Selection</Text>
              </div>

              {requiresDateRange(type) && (
                <Form.Item
                  label="Select Date Range"
                  name="dateRange"
                  rules={[{ required: true, message: 'Please select date range' }]}
                >
                  <RangePicker
                    presets={rangePresets}
                    onChange={rangeDateOnChange}
                    style={{ width: '100%' }}
                    placeholder={['Start Date', 'End Date']}
                  />
                </Form.Item>
              )}

              {requiresSingleDate(type) && (
                <Form.Item
                  label="Select Date"
                  name="reportDate"
                  rules={[{ required: true, message: 'Please select a date' }]}
                >
                  <DatePicker
                    onChange={reportDateOnChange}
                    style={{ width: '100%' }}
                    placeholder="Select date"
                  />
                </Form.Item>
              )}

              {requiresMonthPicker(type) && (
                <Form.Item
                  label="Select Month"
                  name="monthPicker"
                  rules={[{ required: true, message: 'Please select a month' }]}
                >
                  <DatePicker
                    onChange={reportDateOnChange}
                    picker="month"
                    style={{ width: '100%' }}
                    placeholder="Select month"
                  />
                </Form.Item>
              )}
            </Card>

            {/* Email Section (for email-enabled reports) */}
            {requiresEmail(type) && (
              <Card className="mb-4" size="small">
                <div className="flex items-center gap-2 mb-3">
                  <MailOutlined className="text-green-600" />
                  <Text strong>Email Configuration</Text>
                </div>
                <Form.Item
                  label="Email Address"
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input
                    placeholder="Enter your email address"
                    onChange={(e) => setEmail(e.target.value)}
                    prefix={<MailOutlined />}
                  />
                </Form.Item>
                <Text type="secondary" className="text-sm">
                  The report will be sent to this email address once generated.
                </Text>
              </Card>
            )}

            {/* Configuration Section */}
            {(REPORT_TYPES_WITH_FINANCING_TYPE.includes(type as ReportTypeEnum) ||
              REPORT_TYPES_WITH_LOAN_TYPE.includes(type as ReportTypeEnum)) && (
              <Card size="small">
                <div className="flex items-center gap-2 mb-3">
                  <SettingOutlined className="text-orange-600" />
                  <Text strong>Report Configuration</Text>
                </div>

                <Row gutter={16}>
                  {REPORT_TYPES_WITH_FINANCING_TYPE.includes(type as ReportTypeEnum) && (
                    <Col span={12}>
                      <Form.Item
                        label="Financing Type"
                        name="financingType"
                        rules={[
                          {
                            required: true,
                            message: 'Please select financing type',
                          },
                        ]}
                      >
                        <Select
                          onChange={(value) => setFinancingType(value)}
                          placeholder="Select financing type"
                          options={[
                            {
                              value: 'CONVENTIONAL',
                              label: 'Conventional',
                            },
                            {
                              value: 'ISLAMIC',
                              label: 'Islamic',
                            },
                            {
                              value: 'ALL',
                              label: 'All',
                            },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  {REPORT_TYPES_WITH_LOAN_TYPE.includes(type as ReportTypeEnum) && (
                    <Col span={12}>
                      <Form.Item
                        label="Loan Type"
                        name="loanType"
                        rules={[
                          {
                            required: true,
                            message: 'Please select loan type',
                          },
                        ]}
                      >
                        <Select
                          onSelect={(value) => setLoanType(value)}
                          placeholder="Select loan type"
                          options={getLoanTypeOptions(type as ReportTypeEnum)}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </Card>
            )}
          </Form>

          {/* Info Section */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
            <Text type="secondary" className="text-sm">
              <strong>Note:</strong>{' '}
              {requiresEmail(type)
                ? 'This report will be generated asynchronously and sent to your email.'
                : 'The report will be downloaded automatically once generated.'}
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReportTable;
